import { Capacitor, registerPlugin } from '@capacitor/core';
import { API_BASE_URL } from '../services/api';
import { isTWA } from './platform';

/**
 * Store review helpers.
 *
 *  - `requestNativeRating()` shows the store's in-app star sheet in the
 *    native apps: Apple's on iOS, Google Play In-App Review on Android. The
 *    store decides whether it actually appears (quota per person/device) and
 *    the user never leaves the app. Only ReviewPrompt calls it.
 *  - `openWriteReview()` opens the store listing directly on the review
 *    form: the App Store's write-review action on iOS, the Play listing on
 *    Android (the TWA hands a Play URL to the Play app).
 *  - `recordReviewEvent()` tells the server what happened so the ask is
 *    never repeated to someone who answered, and admin can see the funnel.
 */
interface AppReviewPlugin {
  requestReview(): Promise<{ requested: boolean }>;
}

const Plugin = registerPlugin<AppReviewPlugin>('AppReview');

export const APP_STORE_ID = '6762360922';
export const PLAY_PACKAGE = 'com.onrender.fuel_iq.twa';

export type ReviewOutcome = 'shown' | 'up' | 'down' | 'dismissed' | 'wrote';

export const isIosApp = () => Capacitor.getPlatform() === 'ios';
/** The native Android app; AppReviewPlugin.java shows Play's in-app rating sheet. */
export const isAndroidApp = () => Capacitor.getPlatform() === 'android';

const isIosBrowser = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) && !isIosApp();

/** Which store this person can review on. */
export function reviewStore(): 'apple' | 'google' {
  return isIosApp() || isIosBrowser() ? 'apple' : 'google';
}

export function reviewStoreName(): string {
  return reviewStore() === 'apple' ? 'the App Store' : 'Google Play';
}

export async function requestNativeRating(): Promise<boolean> {
  if (!isIosApp() && !isAndroidApp()) return false;
  try {
    const r = await Plugin.requestReview();
    return !!r?.requested;
  } catch {
    return false;
  }
}

export function writeReviewUrl(): string {
  return reviewStore() === 'apple'
    ? `https://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`
    : `https://play.google.com/store/apps/details?id=${PLAY_PACKAGE}&showAllReviews=true`;
}

export function openWriteReview(): void {
  const url = writeReviewUrl();
  if (isTWA() || isIosApp() || isAndroidApp()) {
    // Same-window navigation lets the OS hand the store URL to the store app.
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function fetchReviewEligibility(): Promise<{ eligible: boolean; reason: string } | null> {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const res = await fetch(`${API_BASE_URL}/reviews/eligibility`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const data = await res.json();
    return { eligible: !!data.eligible, reason: String(data.reason || '') };
  } catch {
    return null;
  }
}

export async function recordReviewEvent(outcome: ReviewOutcome, feedback?: string): Promise<void> {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    await fetch(`${API_BASE_URL}/reviews/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ outcome, feedback }),
    });
  } catch {
    /* never block the UI on bookkeeping */
  }
}
